/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/unbound-method */
import { useRouter } from 'next/router'
import React from 'react'
import { CardProps } from 'src/common/components/Card'
import { FilterBar } from 'src/common/components/FilterBar'
import { HeroBanner } from 'src/common/components/HeroBanner'
import { Lane } from 'src/common/components/Lane'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { StatisticsBar } from 'src/common/components/StatisticsBar'
import { EVENT_TYPE } from 'src/common/constants/eventTypes'
import { useIsLoggedIn } from 'src/common/hooks/useIsLoggedIn'
import { useUser } from 'src/common/hooks/useUser'
import { prependAssetHost, prependAssetHostChallenge } from 'src/common/utils'
import { getChallengeBadgeType } from 'src/common/utils/cards'
import { mapSkillLevelString } from 'src/common/utils/challenges'
import { learnMoreChallengeCards } from '../../common/constants/learnMoreChallengeCards'
import { DiscoverLaneRes, getAllChallenges, getLanes } from 'src/services/mainAPI'
import { Challenge } from 'src/services/types/searchEvents'
import { wrapper } from 'src/store'
import { TrustBox } from 'src/common/components/TrustPilotReviews'

type ChallengesPageProps = {
    initialLoggedInStatus: boolean
    allChallenges: Challenge[]
    challengeLanes: DiscoverLaneRes
}

const ChallengesPage = ({ allChallenges, challengeLanes }: ChallengesPageProps) => {
    const router = useRouter()
    const user = useUser()
    const isLoggedInSSR = useIsLoggedIn()
    // const { data: allChallenges } = mainApi.endpoints.getAllChallenges.useQueryState(null)
    const firstChallenge =
        allChallenges?.filter((r) => r.isPrivateChallenge !== true)[0] ?? null

    return (
        <AppLayout
            initialLoggedInStatus={false}
            pageTitle="Challenges"
            metaTitle="Whether you want to target a certain body part or learn something new, our fitness challenges are designed to move you. | Yes.Fit"
        >
            <HeroBanner
                isLoggedIn={isLoggedInSSR}
                bgImage={prependAssetHostChallenge(firstChallenge.imageUrl)}
                buttonText={{
                    loggedIn: 'Join This Challenge',
                    loggedOut: 'Get Access Now'
                }}
                description={null}
                onButtonClick={() => {
                    void router.push(`/challenges/${firstChallenge.challengeUrl}`)
                }}
                subtitle={`${firstChallenge.totalDistance} ${firstChallenge.distanceUnit}`}
                title={firstChallenge.name}
                type="challenge"
            />

            <StatisticsBar
                statItems={[
                    {
                        faIconClasses: 'fas fa-fire',
                        metricText: '37 Thousand',
                        metricDescription: 'workouts completed'
                    },
                    {
                        faIconClasses: 'fas fa-users',
                        metricText: '21 Thousand',
                        metricDescription: 'participants'
                    },
                    {
                        faIconClasses: 'fas fa-fire',
                        metricText: '60+',
                        metricDescription: 'hours of content'
                    }
                ]}
            />

            <FilterBar />

            <section className="mt-5 md:mt-12 md:mb-10 pl-3 md:pl-0">
                <Lane
                    customItemWidth="auto-cols-[290px]"
                    title="Learn more about Challenges"
                    data={learnMoreChallengeCards.map((learnMoreChallenge, index) => {
                        if (index === 0) {
                            return {
                                cardType: 'learnMoreChallenge',
                                imageURL: learnMoreChallenge.imageURL,
                                title: learnMoreChallenge.title,
                                subTitle: learnMoreChallenge.subTitle,
                                featured: true
                            }
                        }
                        return {
                            cardType: 'learnMoreChallenge',
                            imageURL: learnMoreChallenge.imageURL,
                            title: learnMoreChallenge.title,
                            subTitle: learnMoreChallenge.subTitle
                        }
                    })}
                />
            </section>

            {challengeLanes?.lanes?.map((lane, idx) => {
                return (
                    <section className="mt-5 md:mt-12 md:mb-10 pl-3 md:pl-0" key={idx}>
                        <Lane
                            titleIconComponent={() => (
                                <i
                                    aria-hidden
                                    className="fak fa-yf-challenge text-yf-purple text-xl leading-none"
                                ></i>
                            )}
                            title={lane.name}
                            data={
                                lane.challengeLanes
                                    ?.filter((i) => i.challenge !== null)
                                    .map((item): CardProps => {
                                        return {
                                            cardType: 'challenge',
                                            badgeType: getChallengeBadgeType(
                                                item.challenge,
                                                user
                                            ),
                                            duration: `${item.challenge.totalDistance} ${item.challenge.distanceUnit}`,
                                            imageURL: item.challenge.logo
                                                ? prependAssetHostChallenge(
                                                      item.challenge.logo
                                                  )
                                                : prependAssetHost(
                                                      '/icons/no-challenge-bg.png'
                                                  ),
                                            excerciseType: '',
                                            title: item.challenge.name,
                                            trainers: [
                                                {
                                                    intensity: mapSkillLevelString(
                                                        item.challenge.skillLevelString
                                                    ),
                                                    name: item.challenge.skillLevelString
                                                }
                                            ],
                                            href: `/challenges/${encodeURIComponent(
                                                item.challenge.challengeUrl
                                            )}`
                                        }
                                    }) ?? []
                            }
                        />
                    </section>
                )
            })}
            <section className="mb-10 p-6 md:p-14 bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500">
                <h1 className="text-3xl md:text-5xl text-center font-medium text-white pb-8">
                    What Our Users Are Saying
                </h1>
                <div suppressHydrationWarning={true}>
                    {typeof window !== 'undefined' && <TrustBox />}
                </div>
            </section>
        </AppLayout>
    )
}

export const getServerSideProps = wrapper.getServerSideProps((store) => async () => {
    const { data: allChallenges } = await store.dispatch(getAllChallenges.initiate(null))
    const { data: challengeLanes } = await store.dispatch(
        getLanes.initiate(EVENT_TYPE.CHALLENGE)
    )

    const state = store.getState()

    return {
        props: {
            allChallenges,
            initialLoggedInStatus: state.user.data !== null,
            challengeLanes: challengeLanes ?? []
        }
    }
})

export default ChallengesPage
